import type { DocumentHostAction } from "@/domains/documents/types/documentHostAction";
import type { VersionedFolder } from "@/domains/documents/types/folderVersions";
import type { TreeFolder } from "@/domains/documents/types";
import { DocumentListTable } from "@/domains/documents/components/organisms/DocumentListTable";
import type {
  DocumentWithTags,
  UnifiedSearchRow,
} from "@/domains/documents/components/organisms/DocumentListTable";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import type { MoveDragSelection } from "@/domains/documents/utils/documentMoveDrag";
import type { AddVersionsOutcome, StagedVersionItem } from "@/domains/documents/types/addVersions";
import type { UploadSelection } from "@/domains/documents/utils/uploadSelection";

interface ProjectDocumentViewProps {
  /** Project documents to display */
  documents: DocumentWithTags[];
  /** Building ID for document operations */
  buildingId: string;
  /** Called after a signing session completes, so the new container appears
   *  in the current folder without a manual reload. */
  onSigned?: () => void;
  /** Subfolders to display at the top of the document list */
  folders?: TreeFolder[];
  /** Optional upload click handler */
  onUploadClick?: () => void;
  /** Callback to load more documents (infinite scroll) */
  onLoadMore?: () => void;
  /** Whether there are more documents to load */
  hasMore?: boolean;
  /** Whether more documents are currently being loaded */
  isLoadingMore?: boolean;
  /** Callback when a container file (.zip, .asice) is clicked to navigate into */
  onContainerClick?: (docId: string, filename: string) => void;
  /** Callback when a folder is clicked to navigate into */
  onFolderClick?: (folderId: string) => void;
  /** Callback when a folder is renamed */
  onRenameFolder?: (folderId: string, newName: string) => void;
  /** Callback when a folder is deleted */
  onDeleteFolder?: (folderId: string) => void;
  /** Callback when a subfolder is created */
  onCreateSubfolder?: (parentFolderId: string) => void;
  /** Callback when a folder export (zip download) is triggered from its row's action menu */
  onExportFolder?: (folderId: string) => void;
  /** Callback when a folder's "Organize with AI" is triggered (WHA-3654) */
  onOrganizeFolder?: (folderId: string) => void;
  /** Callback when a folder share is triggered from its row's action menu */
  onShareFolder?: (folderId: string) => void;
  /** Callback when share button is clicked */
  onShare?: () => void;
  /** Callback when move/copy is clicked */
  onMoveCopy?: () => void;
  /** Selected folder IDs for folder selection */
  selectedFolderIds?: string[];
  /** Callback when a folder is selected/deselected */
  onFolderSelect?: (folderId: string, isSelected: boolean) => void;
  /** Callback when "Ask Franklin" is clicked in bulk action bar */
  onAskFranklin?: (docs: Array<{ id: string; filename: string }>) => void;
  /** The hosting page's own action on documents — see DocumentListTable. */
  hostAction?: DocumentHostAction;
  /** Whether the list offers its Tags column (default true). */
  showTags?: boolean;
  /** The Brief folder's versions, listed under its row. */
  versionedFolder?: VersionedFolder;
  /** Callback after bulk delete completes (e.g. to refresh folder tree) */
  onBulkDeleteComplete?: () => void;
  /** Whether documents may be moved to the Trash here. See DocumentListTable. */
  canMoveToTrash?: boolean;
  /** Callback after one document is deleted (e.g. to refresh folder counts). */
  onDocumentDeleted?: (docId: string) => void;
  /** Upload a local file as the next version of the target document. */
  onAddNewVersions?: (
    items: StagedVersionItem[],
    doc: DocumentWithTags
  ) => Promise<AddVersionsOutcome>;
  /** Submit handler for inline document rename. */
  onRenameDocumentSubmit?: (docId: string, newFilename: string) => Promise<void> | void;
  /** Predicate to gate the per-row Rename action visibility. */
  canRenameDocument?: (doc: DocumentWithTags) => boolean;
  /** When set, the table renders these rows in the given order instead of
   *  the default folders-then-documents layout (WHA-3185 / WHA-3183 search
   *  results). */
  orderedSearchRows?: UnifiedSearchRow[];
  /** When true, DocumentListTable shows the small spinner overlay over the
   *  document list (WHA-3247). Forwarded to DocumentListTable's `isRefreshing`
   *  prop, which already gates the overlay on `hasRenderedTableRef` so it
   *  doesn't flash on first mount. */
  isRefreshing?: boolean;
  /** True while a folder-selection cascade is still resolving — forwarded to
   *  the bulk action bar so it shows a spinner for the count and disables
   *  actions until every child document is selected. */
  isSelectionPending?: boolean;
  /** Full project hierarchy plus native move callbacks for the Folders view. */
  dragDropFolders?: TreeFolder[];
  onDropDocument?: (documentId: string, targetFolderId: string | null) => void;
  onMoveFolder?: (folderId: string, targetFolderId: string | null) => void;
  onDropSelection?: (docIds: string[], folderIds: string[], targetFolderId: string | null) => void;
  onDropFiles?: (selection: UploadSelection, targetFolderId: string | null) => void;
  onInternalDragChange?: (selection: MoveDragSelection | null) => void;
  /** The view-wide drag, so drags started in the folder tree also light up (and
   *  correctly refuse) the table's folder rows. */
  activeMove?: MoveDragSelection | null;
  /** Whether filters or search narrow the rows. See DocumentListTable. */
  hasActiveFilters?: boolean;
  /** The empty state's Clear filters action. See DocumentListTable. */
  onClearFilters?: () => void;
}

export function ProjectDocumentView({
  documents,
  buildingId,
  onSigned,
  folders = [],
  onUploadClick,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  onContainerClick,
  onFolderClick,
  onRenameFolder,
  onDeleteFolder,
  onCreateSubfolder,
  onExportFolder,
  onOrganizeFolder,
  onShareFolder,
  onShare,
  onMoveCopy,
  selectedFolderIds = [],
  onFolderSelect,
  onAskFranklin,
  hostAction,
  showTags = true,
  versionedFolder,
  onBulkDeleteComplete,
  canMoveToTrash,
  onDocumentDeleted,
  onAddNewVersions,
  onRenameDocumentSubmit,
  canRenameDocument,
  orderedSearchRows,
  isRefreshing = false,
  isSelectionPending = false,
  dragDropFolders,
  onDropDocument,
  onMoveFolder,
  onDropSelection,
  onDropFiles,
  onInternalDragChange,
  activeMove,
  hasActiveFilters,
  onClearFilters,
}: ProjectDocumentViewProps) {
  const documentStore = useDocumentStore();

  return (
    // No inner padding — the mounting page provides the page gutter and the
    // table rows carry their own px-6, so the table sits flush with the
    // folder tree.
    <div className="space-y-4">
      {/* Document List Table with folders at the top */}
      <DocumentListTable
        buildingId={buildingId}
        store={documentStore}
        documents={documents}
        folders={folders}
        showStatus={false}
        enableSigning
        onSigned={onSigned}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onContainerClick={onContainerClick}
        onFolderClick={onFolderClick}
        onRenameFolder={onRenameFolder}
        onDeleteFolder={onDeleteFolder}
        onCreateSubfolder={onCreateSubfolder}
        onExportFolder={onExportFolder}
        onOrganizeFolder={onOrganizeFolder}
        onShareFolder={onShareFolder}
        onShare={onShare}
        onMoveCopy={onMoveCopy}
        selectedFolderIds={selectedFolderIds}
        onFolderSelect={onFolderSelect}
        onAskFranklin={onAskFranklin}
        hostAction={hostAction}
        showTags={showTags}
        versionedFolder={versionedFolder}
        onBulkDeleteComplete={onBulkDeleteComplete}
        canMoveToTrash={canMoveToTrash}
        onDocumentDeleted={onDocumentDeleted}
        onAddNewVersions={onAddNewVersions}
        onRenameDocumentSubmit={onRenameDocumentSubmit}
        canRenameDocument={canRenameDocument}
        orderedSearchRows={orderedSearchRows}
        isRefreshing={isRefreshing}
        isSelectionPending={isSelectionPending}
        dragDropFolders={dragDropFolders}
        onDropDocument={onDropDocument}
        onMoveFolder={onMoveFolder}
        onDropSelection={onDropSelection}
        onDropFiles={onDropFiles}
        onInternalDragChange={onInternalDragChange}
        activeMove={activeMove}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />
    </div>
  );
}
